import { useEffect } from "react";
import { motion } from "framer-motion";
import { useUi } from "../stores/ui";

export function AnimatedNumber({ value }: { value: number }) {
  return (
    <motion.span
      key={value}
      initial={{ y: 8, opacity: 0.4 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.35 }}
    >
      {value}
    </motion.span>
  );
}

export function ProgressBar({ value }: { value: number }) {
  const width = Math.max(0, Math.min(100, value));
  return (
    <div className="h-2 overflow-hidden rounded-full bg-white/10">
      {width > 0 ? (
        <motion.div
          className="h-full rounded-full bg-accent"
          initial={{ width: 0 }}
          animate={{ width: `${width}%` }}
          transition={{ duration: 0.6 }}
        />
      ) : null}
    </div>
  );
}

export function CircularProgress({ value, label }: { value: number; label: string }) {
  const r = 36;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, value));
  return (
    <div className="flex items-center gap-4">
      <svg width="96" height="96" viewBox="0 0 96 96" className="-rotate-90">
        <circle cx="48" cy="48" r={r} stroke="rgba(255,255,255,0.08)" strokeWidth="8" fill="none" />
        <motion.circle
          cx="48"
          cy="48"
          r={r}
          stroke="rgb(var(--accent))"
          strokeWidth="8"
          fill="none"
          strokeLinecap="round"
          strokeDasharray={c}
          initial={{ strokeDashoffset: c }}
          animate={{ strokeDashoffset: c - (pct / 100) * c }}
        />
      </svg>
      <div>
        <p className="text-3xl font-semibold">{Math.round(pct)}%</p>
        <p className="text-sm text-zinc-400">{label}</p>
      </div>
    </div>
  );
}

export function Toasts() {
  const toasts = useUi((s) => s.toasts);
  const dismiss = useUi((s) => s.dismissToast);
  return (
    <div className="pointer-events-none fixed right-3 top-3 z-[80] flex w-[min(22rem,calc(100vw-1.5rem))] flex-col gap-2 sm:right-4 sm:top-4">
      {toasts.map((item) => {
        const tone =
          item.tone === "error"
            ? "border-rose-500/40 bg-ink-900"
            : item.tone === "success"
              ? "border-accent/50 bg-ink-900"
              : "border-white/15 bg-ink-900";
        const bar =
          item.tone === "error" ? "bg-rose-500" : item.tone === "success" ? "bg-accent" : "bg-cyan";
        return (
          <button
            key={item.id}
            type="button"
            className={`pointer-events-auto overflow-hidden rounded-lg border text-left shadow-lg shadow-black/40 ${tone}`}
            onClick={() => dismiss(item.id)}
          >
            <span className={`block h-0.5 w-full ${bar}`} />
            <span className="block p-3.5">
              <p className="text-sm font-semibold text-white">{item.title}</p>
              {item.body ? <p className="mt-1 text-xs leading-relaxed text-zinc-400">{item.body}</p> : null}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export function Modal({
  open,
  title,
  onClose,
  children,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 flex items-end justify-center bg-black/60 p-3 sm:items-center sm:p-4">
      <button className="absolute inset-0" aria-label="Close dialog" onClick={onClose} />
      <div role="dialog" aria-modal="true" aria-label={title} className="relative z-10 max-h-[min(92dvh,40rem)] w-full max-w-lg overflow-y-auto rounded-t-xl border border-white/10 bg-ink-900 p-5 sm:rounded-lg sm:p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button className="focus-ring rounded-lg px-2 py-1 text-zinc-400" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
